import { useQuery } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";

export type ChatModelOption = {
  /** Full router id, e.g. "openai/gpt-4o". */
  id: string;
  /** Provider segment, mapped to a brand logo in the UI. */
  provider: string;
  /** Human-friendly display name, e.g. "GPT 4o". */
  label: string;
};

type ChatModelsResponse = {
  models: ChatModelOption[];
  /** Null when the service has no provider key set for any configured model. */
  defaultModel: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isChatModelOption(value: unknown): value is ChatModelOption {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.provider === "string" &&
    typeof value.label === "string"
  );
}

/** Validate the /api/config/models payload at the trust boundary. */
function parseChatModelsResponse(data: unknown): ChatModelsResponse {
  const models =
    isRecord(data) && Array.isArray(data.models)
      ? data.models.filter(isChatModelOption)
      : [];
  const defaultModel =
    isRecord(data) && typeof data.defaultModel === "string"
      ? data.defaultModel
      : null;
  return { models, defaultModel };
}

type ChatModelState = {
  models: ChatModelOption[];
  selectedModel: string | null;
  setSelectedModel: (id: string) => void;
  /**
   * True until the catalog has confirmed no provider is configured. Stays true
   * while the catalog is still loading so the composer doesn't briefly flash
   * an "unconfigured" state.
   */
  isConfigured: boolean;
};

const STORAGE_KEY_PREFIX = "arcade:chat-model";

export function useChatModelState({
  baseUrl,
  organizationId,
  projectId,
  viewerId,
}: {
  baseUrl: string;
  organizationId: string | null;
  projectId: string | null;
  viewerId: string;
}): ChatModelState {
  const storageKey = `${STORAGE_KEY_PREFIX}:${organizationId ?? ""}:${projectId ?? ""}:${viewerId}`;
  const [persistedModel, setSelectedModel] = useSessionStorage<string | null>(
    storageKey,
    null
  );

  const { data, isError } = useQuery({
    queryKey: ["chat-models", baseUrl],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/api/config/models`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch models: ${res.status}`);
      }
      return parseChatModelsResponse(await res.json());
    },
    staleTime: 5 * 60 * 1000,
  });

  const models = data?.models ?? [];

  // Trust the persisted id until the catalog is actually loaded — otherwise
  // the empty-array in-flight state falsely reads as "stored model was dropped
  // from the allowlist" and the request omits `model`, so the server picks
  // its default instead of the user's stored choice. Once data arrives, only
  // keep the stored id if it's still offered; the server also allowlists.
  const selectedModel = resolveSelectedModel(persistedModel, data);
  // Optimistic only while genuinely loading (no data, no error) so the
  // composer doesn't flash "unconfigured". A failed fetch with no cached
  // catalog counts as unconfigured — otherwise the composer stays enabled and
  // sends would 503. Once data arrives, `defaultModel === null` is the
  // server's explicit "no provider configured" signal.
  const isConfigured =
    data === undefined ? !isError : data.defaultModel !== null;

  return { models, selectedModel, setSelectedModel, isConfigured };
}

function resolveSelectedModel(
  persisted: string | null,
  data: ChatModelsResponse | undefined
): string | null {
  if (data === undefined) {
    return persisted;
  }
  if (persisted != null && data.models.some((m) => m.id === persisted)) {
    return persisted;
  }
  return data.defaultModel ?? null;
}
